import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} FinanceOptionsRefs
 * @property {HTMLElement} content
 */

/**
 * Keeps the finance figures in step with the selected variant.
 *
 * A plain variant change on the product page morphs only the variant picker, then fires
 * `variant:update` carrying the freshly rendered section HTML. Everything price-dependent
 * pulls its own replacement out of that payload - see product-price.js, which this
 * follows deliberately.
 *
 * @extends {Component<FinanceOptionsRefs>}
 */
class FinanceOptions extends Component {
  requiredRefs = ['content'];

  connectedCallback() {
    super.connectedCallback();
    const section = this.closest('.shopify-section, dialog');
    if (!section) return;
    section.addEventListener(ThemeEvents.variantUpdate, this.#updateFinance);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const section = this.closest('.shopify-section, dialog');
    if (!section) return;
    section.removeEventListener(ThemeEvents.variantUpdate, this.#updateFinance);
  }

  #updateFinance = (/** @type {any} */ event) => {
    const newProduct = event.detail?.data?.newProduct;

    if (newProduct) {
      this.dataset.productId = newProduct.id;
    } else if (
      event.target instanceof HTMLElement &&
      event.target.dataset.productId &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      // A different product on the same page (a card, a quick-add dialog) changed variant.
      return;
    }

    const html = event.detail?.data?.html;
    if (!html) return;

    const { content } = this.refs;
    const incoming = html.querySelector(`finance-options[data-block-id="${this.dataset.blockId}"]`);

    if (!incoming) {
      // The new variant is not eligible for any plan, so the block renders nothing.
      content.replaceChildren();
      return;
    }

    const replacement = incoming.querySelector('[ref="content"]');
    if (!replacement || !content) return;

    // Carry the disclosure's open state across the swap, or expanding the panel and then
    // picking a variant would silently collapse it.
    const wasOpen = content.querySelector('details')?.open ?? false;
    content.replaceWith(replacement);
    const details = replacement.querySelector('details');
    if (details) details.open = wasOpen;
  };
}

if (!customElements.get('finance-options')) {
  customElements.define('finance-options', FinanceOptions);
}
